import React, { useState, ReactNode } from "react";
import classNames from "classnames";

interface CollapseProps {
  title: ReactNode;
  children: ReactNode;
  defaultOpen?: boolean;
  isOpen?: boolean;
  onToggle?: () => void;
  className?: string;
  headerClassName?: string;
  contentClassName?: string;
  variant?: "glass" | "plain" | "surface";
  density?: "comfortable" | "compact" | "dense";
}

export const Collapse = ({
  title,
  children,
  defaultOpen = false,
  isOpen: propIsOpen,
  onToggle,
  className,
  headerClassName,
  contentClassName,
  variant = "glass",
  density = "compact",
}: CollapseProps) => {
  const [localIsOpen, setLocalIsOpen] = useState(defaultOpen);

  const isControlled = propIsOpen !== undefined;
  const isOpen = isControlled ? propIsOpen : localIsOpen;

  const handleToggle = () => {
    if (onToggle) {
      onToggle();
    } else {
      setLocalIsOpen(!localIsOpen);
    }
  };

  const densityPaddings = {
    comfortable: "p-5 md:p-6",
    compact: "p-4",
    dense: "p-3",
  };

  const variantClasses = {
    glass: {
      container:
        "border-white/20 dark:border-primary-800/70 bg-white/20 dark:bg-gradient-to-b dark:from-content-900 dark:to-primary-950/70 backdrop-blur-xl shadow-[0_8px_30px_-12px_rgba(15,23,42,0.15)]",
      button: "hover:bg-white/10 dark:hover:bg-primary-500/10",
      content:
        "border-none bg-white/5 dark:bg-content-950/20",
    },
    plain: {
      container:
        "border-content-200 dark:border-content-700/70 bg-white dark:bg-content-900 shadow-sm",
      button: "hover:bg-base-50 dark:hover:bg-content-800",
      content:
        "border-none bg-base-50/30 dark:bg-content-950/20",
    },
    surface: {
      container: "border-transparent bg-primary-50/50 dark:bg-content-800/75",
      button: "hover:bg-primary-500/10 dark:hover:bg-content-700/50",
      content:
        "border-none bg-primary-50/20 dark:bg-content-800/30",
    },
  };

  const classes = variantClasses[variant];
  const padding = densityPaddings[density];

  return (
    <div
      className={classNames(
        "font-sans rounded-xl border border-solid overflow-hidden transition-all duration-200",
        classes.container,
        className,
      )}
    >
      <button
        type="button"
        onClick={handleToggle}
        aria-expanded={isOpen}
        className={classNames(
          "appearance-none border-none outline-none shadow-none m-0",
          "flex w-full items-center justify-between text-left font-medium transition-colors cursor-pointer",
          "text-content-900 dark:text-content-100",
          "bg-transparent focus-visible:ring-2 focus-visible:ring-primary-500/55",
          padding,
          classes.button,
          headerClassName,
        )}
      >
        <div className="flex-1">{title}</div>
        <div
          className={classNames(
            "icon-[ph--caret-down] h-4 w-4 transition-transform duration-200 text-content-500 ml-3 flex-shrink-0",
            isOpen && "rotate-180",
          )}
        />
      </button>

      {isOpen && (
        <div
          className={classNames(
            "border-solid",
            padding,
            classes.content,
            contentClassName,
          )}
        >
          {children}
        </div>
      )}
    </div>
  );
};
